"use client"

/**
 * QuestionComposerInspector — the right rail: Leo jobs, then Properties
 * (Ready, format, locations, tags, classification).
 */

import * as React from "react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Tip } from "@/components/ui/tip"
import { QuestionLeoJobs } from "@/components/question-quality/question-leo-jobs"
import { QuestionLeoPerformanceInsight } from "@/components/question-quality/question-leo-performance-insight"
import { QuestionQualityPane } from "@/components/question-quality/question-quality-pane"
import { ToggleSwitch } from "@/components/ui/toggle-switch"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import {
  leoAuthoringJobs,
  leoJobsNote,
  noteContainsUsagePhrase,
  questionQualityVisibleHomes,
} from "@/lib/question-quality"
import { ComposerPanelCard } from "@/components/templates/composer-panel-card"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { initialsFromDisplayName } from "@/lib/initials-from-name"
import { NAV_USER } from "@/lib/mock/navigation"
import {
  COMPOSER_FIELD_LABEL_CLASS,
  ComposerTagsField,
  FolderPickerControl,
  InspectorSection,
} from "./composer-fields"
import {
  ComposerClassificationFields,
  ComposerFormatField,
} from "./composer-item-identity"
import {
  ComposerCategoryDrillIn,
  ComposerCategoryHeader,
} from "./composer-category-drill-in"
import {
  useQuestionComposerContext,
  useQuestionComposerInspectorSurface,
  useQuestionComposerPageScroll,
} from "./composer-context"

function CompactRailDot() {
  return (
    <span
      className="absolute -top-1 -right-1 size-1.5 rounded-full bg-destructive"
      aria-hidden="true"
    />
  )
}

export function QuestionInspectorCompactNav({
  density = "rail",
}: {
  density?: "rail" | "card"
}) {
  const { hasOpenReviewChecks, openLeoSpotted, openProperties } =
    useQuestionComposerContext()
  const rail = density === "rail"
  const buttonClass = rail
    ? "size-9 shrink-0 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
    : cn(
        "size-11 shrink-0 rounded-xl border-border shadow-sm",
        "bg-[var(--secondary-panel-bg)] text-sidebar-foreground",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      )

  const leoButton = (
    <Tip label="Leo AI" side="left">
      <Button
        type="button"
        size={rail ? "icon" : "icon-lg"}
        variant={rail ? "ghost" : "outline"}
        className={buttonClass}
        aria-label={
          hasOpenReviewChecks ? "Leo AI. Review checks are open." : "Leo AI"
        }
        onClick={openLeoSpotted}
      >
        <span className="relative">
          <i
            className="fa-duotone fa-solid fa-star-christmas text-md leading-none text-brand"
            aria-hidden="true"
          />
          {hasOpenReviewChecks ? <CompactRailDot /> : null}
        </span>
      </Button>
    </Tip>
  )

  const propertiesButton = (
    <Tip label="Properties" side="left">
      <Button
        type="button"
        size={rail ? "icon" : "icon-lg"}
        variant={rail ? "ghost" : "outline"}
        className={buttonClass}
        aria-label="Properties"
        onClick={openProperties}
      >
        <i
          className="fa-light fa-sliders text-md leading-none"
          aria-hidden="true"
        />
      </Button>
    </Tip>
  )

  if (rail) {
    return (
      <>
        <li className="flex w-full justify-center" role="none">
          {leoButton}
        </li>
        <li className="flex w-full justify-center" role="none">
          {propertiesButton}
        </li>
      </>
    )
  }

  return (
    <div className="flex w-full flex-col items-center gap-1 py-1">
      {leoButton}
      {propertiesButton}
    </div>
  )
}

const INSPECTOR_BLOCK_TITLE_CLASS =
  "m-0 min-w-0 py-0.5 text-base font-semibold tracking-tight text-sidebar-foreground"

function ComposerCreatorHistory({
  creator,
}: {
  creator?: { name: string; email?: string }
}) {
  const person = creator?.name?.trim()
    ? creator
    : { name: NAV_USER.name, email: NAV_USER.email }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium text-foreground">Creator and history</p>
      <div className="flex w-full items-center gap-3 rounded-2 border border-border p-3">
        <span className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
          <Avatar shape="circle" className="shrink-0">
            <AvatarFallback className="text-xs font-semibold">
              {initialsFromDisplayName(person.name).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-sm leading-5 font-medium text-foreground">
              {person.name}
            </span>
            {person.email ? (
              <span className="truncate text-sm leading-5 text-muted-foreground">
                {person.email}
              </span>
            ) : null}
          </span>
        </span>
      </div>
    </div>
  )
}

export function QuestionComposerInspectorFields() {
  const {
    form,
    inspectorLead,
    disabled,
    folders,
    onRequestNewFolder,
    onReadyChange,
    onReplaceFromQuality,
    inspectorView,
    setInspectorView,
    focusCheckId,
    qualityChecks,
    qualityHistory,
    qualitySnapshot,
    applyQualityCheck,
    dismissQualityCheck,
    enhanceWithLeo,
    openQuestionQuality,
    openProperties,
    folderPickerOpen,
    setFolderPickerOpen,
    creator,
    tagDraft,
    setTagDraft,
    watchedTags,
    watchedStatus,
    commitTag,
    removeTag,
  } = useQuestionComposerContext()

  const isReady = watchedStatus === "in_review"
  const leoJobs = React.useMemo(
    () => leoAuthoringJobs(qualityChecks, qualityHistory, qualitySnapshot),
    [qualityChecks, qualityHistory, qualitySnapshot]
  )
  const jobsNote = React.useMemo(
    () => (leoJobs.length > 0 ? leoJobsNote(leoJobs) : ""),
    [leoJobs]
  )
  const visibleQualityHomes = React.useMemo(
    () => questionQualityVisibleHomes(qualityChecks),
    [qualityChecks]
  )
  const showPerformanceInsight =
    Boolean(qualityHistory?.sat) &&
    visibleQualityHomes.length === 0 &&
    !noteContainsUsagePhrase(jobsNote)

  const askLeoFromJobs = React.useCallback(() => {
    openQuestionQuality()
  }, [openQuestionQuality])

  if (inspectorView === "categories") {
    return <ComposerCategoryDrillIn />
  }

  return (
    <fieldset disabled={disabled} className="contents">
      <section className="flex flex-col gap-6" aria-label="Leo spotted">
        {inspectorView !== "quality" ? (
          <>
            <QuestionLeoJobs
              jobs={leoJobs}
              history={qualityHistory}
              disabled={disabled}
              onAskLeo={askLeoFromJobs}
            />
            {showPerformanceInsight && qualityHistory ? (
              <QuestionLeoPerformanceInsight history={qualityHistory} />
            ) : null}
          </>
        ) : (
          <QuestionQualityPane
            checks={qualityChecks}
            history={qualityHistory}
            focusCheckId={focusCheckId}
            disabled={disabled}
            onBack={() => setInspectorView("settings")}
            onUse={applyQualityCheck}
            onDismiss={dismissQualityCheck}
            onEnhanceWithLeo={enhanceWithLeo}
            onReplace={onReplaceFromQuality}
            onRetagDifficulty={openProperties}
          />
        )}
      </section>

      <section
        className="flex flex-col gap-6"
        aria-labelledby="question-inspector-properties-heading"
      >
        <h2
          id="question-inspector-properties-heading"
          className={INSPECTOR_BLOCK_TITLE_CLASS}
        >
          Properties
        </h2>
        {onReadyChange ? (
          <div className="flex items-center justify-between gap-3 border-b border-border pb-4">
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Label
                htmlFor="qb-mark-ready"
                className="text-sm font-medium text-foreground"
              >
                Question Ready
              </Label>
              <p className="text-xs leading-snug text-muted-foreground">
                {isReady
                  ? "Eligible for the assessment pool. Save keeps Ready."
                  : "Draft until you turn this on. Save writes the badge status."}
              </p>
            </div>
            <ToggleSwitch
              id="qb-mark-ready"
              checked={isReady}
              onChange={onReadyChange}
            />
          </div>
        ) : null}
        {inspectorLead}
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <ComposerFormatField />
            <FormField
              control={form.control}
              name="folderIds"
              render={({ field }) => (
                <FormItem className="gap-1.5">
                  <Label className={COMPOSER_FIELD_LABEL_CLASS}>
                    Locations
                  </Label>
                  <FormControl>
                    <FolderPickerControl
                      folders={folders}
                      value={field.value}
                      onChange={(v) => field.onChange(v)}
                      open={folderPickerOpen}
                      onOpenChange={setFolderPickerOpen}
                      onRequestNewFolder={() => {
                        setFolderPickerOpen(false)
                        onRequestNewFolder?.()
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <InspectorSection title="Tags" htmlFor="qb-tag-input">
            <ComposerTagsField
              id="qb-tag-input"
              tags={watchedTags}
              draft={tagDraft}
              disabled={disabled}
              onDraftChange={setTagDraft}
              onCommit={commitTag}
              onRemove={removeTag}
            />
          </InspectorSection>

          <InspectorSection title="Classification">
            <ComposerClassificationFields />
          </InspectorSection>

          <ComposerCreatorHistory creator={creator} />
        </div>
      </section>
    </fieldset>
  )
}

export function QuestionComposerInspector() {
  const { form, inspectorOpen, setInspectorOpen, inspectorView } =
    useQuestionComposerContext()
  const pageScroll = useQuestionComposerPageScroll()
  const inspectorSurface = useQuestionComposerInspectorSurface()

  if (inspectorSurface === "secondary-panel") {
    return null
  }

  const hideInspectorButton = (
    <Tip side="bottom" label="Collapse to icons">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => setInspectorOpen(false)}
        aria-label="Collapse to icons"
        aria-expanded={true}
      >
        <i className="fa-light fa-angles-right" aria-hidden="true" />
      </Button>
    </Tip>
  )

  return (
    <Form {...form}>
      {!inspectorOpen ? (
        <QuestionInspectorCompactNav density="card" />
      ) : (
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <ComposerPanelCard
            pageScrollBody={pageScroll}
            fillHeight
            title="Leo spotted"
            header={
              inspectorView === "categories" ? (
                <ComposerCategoryHeader titleClassName="text-sm" />
              ) : undefined
            }
            headerActions={hideInspectorButton}
          >
            <div className="flex flex-col gap-8">
              <QuestionComposerInspectorFields />
            </div>
          </ComposerPanelCard>
        </div>
      )}
    </Form>
  )
}
